import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api"; // Import your existing API instance
import "../assets/styles/NetworkError.css";

const NetworkError = ({ homePath = "/dashboard" }) => {
  const { t } = useTranslation("errors");
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const MAX_RETRIES = 7;
  const TIME_WINDOW = 60000; // 1 minute

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Clean up old attempts (older than 1 minute)
  const getValidAttempts = () => {
    const now = Date.now();
    return retryAttempts.filter((attempt) => now - attempt < TIME_WINDOW);
  };

  const checkServerStatus = async () => {
    try {
      const response = await api.get("/api/app-config/server-status");
      return response.data?.data?.status === "ok";
    } catch (error) {
      console.error("Server status check failed:", error);
      return false;
    }
  };

  const handleRetry = async () => {
    const validAttempts = getValidAttempts();

    if (validAttempts.length >= MAX_RETRIES) {
      return;
    }

    setIsRetrying(true);
    const now = Date.now();
    setRetryAttempts((prev) => [...validAttempts, now]);

    try {
      const serverOk = await checkServerStatus();
      if (serverOk) {
        // Redirect to dashboard or previous page
        window.location.href = homePath;
      } else {
        setTimeout(() => setIsRetrying(false), 1500);
      }
    } catch (error) {
      setTimeout(() => setIsRetrying(false), 1500);
    }
  };

  const validAttempts = getValidAttempts();
  const remainingAttempts = MAX_RETRIES - validAttempts.length;
  const canRetry = remainingAttempts > 0;

  // Calculate time until next attempt becomes available
  const getTimeUntilNextAttempt = () => {
    if (validAttempts.length === 0) return 0;

    const oldestAttempt = Math.min(...validAttempts);
    const timeUntilReset = TIME_WINDOW - (Date.now() - oldestAttempt);

    return Math.max(0, timeUntilReset);
  };

  const timeUntilReset = getTimeUntilNextAttempt();

  return (
    <div className="network-error-container">
      <div className="error-content">
        <div className="icon-container">
          <div className="network-icon">
            <div className="signal-bar bar-1"></div>
            <div className="signal-bar bar-2"></div>
            <div className="signal-bar bar-3"></div>
            <div className="signal-bar bar-4"></div>
            <div className="disconnect-line"></div>
          </div>
        </div>

        <h1 className="error-title">{t("networkError.title")}</h1>
        <p className="error-description">
          {t("networkError.description")}
        </p>

        <div className={`connection-status ${isOnline ? "online" : "offline"}`}>
          <div className="status-indicator"></div>
          <span>{isOnline ? t("networkError.deviceOnline") : t("networkError.deviceOffline")}</span>
        </div>  

        <div className="retry-info">
          <p>
            {t("networkError.retryAttemptsRemaining")}: {remainingAttempts}/{MAX_RETRIES}
          </p>
          {!canRetry && timeUntilReset > 0 && (
            <p className="cooldown-text">
              {t("networkError.nextRetryIn")}: {Math.ceil(timeUntilReset / 1000)}s
            </p>
          )}
          {!canRetry && timeUntilReset === 0 && (
            <p className="reset-text">
              {t("networkError.retryLimitReachedWait")}
            </p>
          )}
        </div>

        <div className="action-buttons">
          <button
            className={`retry-button ${isRetrying ? "retrying" : ""}`}
            onClick={handleRetry}
            disabled={isRetrying || !canRetry}
          >
            {isRetrying ? (
              <>
                <div className="spinner"></div>
                {t("networkError.retrying")}
              </>
            ) : (
              <>
                <svg
                  className="retry-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M1 4v6h6M23 20v-6h-6" />
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                </svg>
                {canRetry ? t("networkError.tryAgain") : t("networkError.retryLimitReached")}
              </>
            )}
          </button>
        </div>

        <div className="troubleshooting">
          <details>
            <summary>{t("networkError.troubleshootingTips")}</summary>
            <ul>
              <li>{t("networkError.tips.checkConnection")}</li>
              <li>{t("networkError.tips.waitAndTry")}</li>
              <li>{t("networkError.tips.disableVpn")}</li>
              <li>{t("networkError.tips.clearCache")}</li>
              <li>{t("networkError.tips.differentBrowser")}</li>
              <li>{t("networkError.tips.contactSupport")}</li>
            </ul>
          </details>
        </div>
      </div>
    </div>
  );
};

export default NetworkError;
